import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import {
    applyTrialistExtension,
    sendPurchaseEmail,
    markTrialistExpired,
    getBannerContentByScenario,
    getPurchasesBySchool,
    getActiveBannerContents,
    getCountryId
} from "../services/APIClient";
import "./FreeTrialBanner.css";
import { BigSpinner } from "../services/SpinnerComponent";

function FreeTrialBanner({ schoolid, teacherName, freeDaysLeft, trialistDetails, pilotData = null, autoOpen = false }) {
    // Modal states
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showExpiredModal, setShowExpiredModal] = useState(false);

    // Form states
    const [forwardEmail, setForwardEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Content states
    const [bannerContent, setBannerContent] = useState(null);
    const [contentLoading, setContentLoading] = useState(true);
    const [contentError, setContentError] = useState(null);
    const [fetchedLatestPurchase, setFetchedLatestPurchase] = useState(null);
    const [purchasesLoaded, setPurchasesLoaded] = useState(false);
    const [leadInDayThreshold, setLeadInDayThreshold] = useState(null);
    const [countryId, setCountryId] = useState(null);
    
    // Extract purchase years from trialistDetails
    const purchased_2425 = trialistDetails?.purchased_2425 || false;
    const purchased_2526 = trialistDetails?.purchased_2526 || false;
    const purchased_2627 = trialistDetails?.purchased_2627 || false;

    const nextPaymentDate = fetchedLatestPurchase?.next_payment_date
        ? new Date(fetchedLatestPurchase.next_payment_date)
        : null;

    const hasValidPurchaseDate =
        nextPaymentDate &&
        !Number.isNaN(nextPaymentDate.getTime()) &&
        new Date() <= nextPaymentDate;

    // Extract pilot end date
    const isPilot = pilotData && pilotData.length > 0 && pilotData[0].active;    
    const pilotEndDate = isPilot && pilotData[0]?.end_date    
        ? new Date(pilotData[0].end_date) 
        : null;
    const pilotConverted = isPilot && pilotData[0]?.converted;

    const hasValidPilotDate =
        pilotEndDate &&
        !Number.isNaN(pilotEndDate.getTime()) &&
        new Date() <= pilotEndDate;

    // Determine if user has any previous purchase
    const hasPreviousPurchase = purchased_2425 || purchased_2526 || purchased_2627;
    const hasCurrentPurchase = fetchedLatestPurchase ? hasValidPurchaseDate : false;

    // Determine last purchase year for interpolation
    const getLastPurchaseYear = () => {
        if (purchased_2425) return "2024/25";
        return null;
    };

    // Fetch latest purchase data when schoolid changes
    useEffect(() => {
        const fetchLatestPurchase = async () => {
            if (!schoolid) return;

            try {
                // Get purchases sorted by next_payment_date descending (latest first)
                const purchases = await getPurchasesBySchool(schoolid, {
                    where: { next_payment_date: { neq: null } },
                    order: "next_payment_date DESC",
                    limit: 1
                });

                if (purchases && purchases.length > 0) {
                    setFetchedLatestPurchase(purchases[0]);
                } else {
                    setFetchedLatestPurchase(null);
                }
            } catch (error) {
                console.error("Error fetching latest purchase:", error);
                setFetchedLatestPurchase(null);
            } finally {
                setPurchasesLoaded(true);
            }
        };

        fetchLatestPurchase();
    }, [schoolid]);

    // Compute days until next payment and renewal window
    const daysUntilPayment = fetchedLatestPurchase?.next_payment_date
        ? Math.ceil((new Date(fetchedLatestPurchase.next_payment_date) - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    const isInRenewalWindow = daysUntilPayment !== null
        && leadInDayThreshold !== null
        && daysUntilPayment >= 0
        && daysUntilPayment <= leadInDayThreshold;

    // Compute days until pilot end
    const daysUntilPilotEnd = pilotEndDate
        ? Math.ceil((pilotEndDate - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    const isInPilotConversionWindow = daysUntilPilotEnd !== null
        && leadInDayThreshold !== null
        && daysUntilPilotEnd >= 0
        && daysUntilPilotEnd <= leadInDayThreshold;

    // Determine scenario type based on props
    const getScenarioType = useCallback(() => {
        // Pilot school within conversion window
        if (isPilot && !pilotConverted && isInPilotConversionWindow) {
            return "pilot_purchase";
        }

        // Pilot outside conversion window — no banner
        if (isPilot) {
            return null;
        }

        // Pure trialist — no purchase history, no pilot
        if (!hasPreviousPurchase && !hasCurrentPurchase) {
            return freeDaysLeft > 0 ? "trial_active" : "trial_expired";
        }

        // Has any purchase history, not yet purchased_2627
        if (hasPreviousPurchase && !purchased_2627) {
            // next_payment_date has already passed - lapsed customer
            if (daysUntilPayment !== null && daysUntilPayment < 0) {
                return "lapsed_customer";
            }
            return "subscriber_renewal";
        }

        return null; // Has current purchase or pilot - banner won't show
    }, [isPilot, pilotConverted, isInPilotConversionWindow, hasPreviousPurchase, hasCurrentPurchase, freeDaysLeft, purchased_2627, daysUntilPayment]);

    // Interpolation helper - replaces {{placeholder}} with values
    const interpolate = (text, values) => {
        if (!text) return "";
        return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
            return values[key] !== undefined ? values[key] : match;
        });
    };

    const scenarioType = getScenarioType();
    // Values for interpolation
    const interpolationValues = {
        days: scenarioType === 'pilot_purchase' ? daysUntilPilotEnd ?? daysUntilPayment ?? freeDaysLeft : (daysUntilPayment ?? freeDaysLeft),
        lastPurchaseYear: getLastPurchaseYear(),
        endDate: trialistDetails?.end_date
            ? new Date(trialistDetails.end_date).toLocaleDateString('en-IE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
            : "",
        pilotEndDate: pilotEndDate
            ? pilotEndDate.toLocaleDateString('en-IE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
            : "",
        pilot_converted: pilotConverted === true,
        pilot_end_date: pilotEndDate ? pilotEndDate.toISOString() : null
    };

    // Fetch banner content on mount
    useEffect(() => {
        const fetchContent = async () => {
            const scenarioType = getScenarioType();

            if (!scenarioType) {
                setContentLoading(false);
                return;
            }

            try {
                // subscriber_renewal and pilot_purchase are owned by Fetch 2 (getActiveBannerContents)
                if (scenarioType === 'subscriber_renewal' || scenarioType === 'pilot_purchase') {
                    setContentLoading(false);
                    return;
                }
                setContentLoading(true);
                const response = await getBannerContentByScenario(scenarioType);
                setBannerContent(response.data.content.content);
                setContentError(null);
            } catch (error) {
                console.error("Failed to fetch banner content:", error);
                setContentError("Failed to load banner content");
            } finally {
                setContentLoading(false);
            }
        };

        if (trialistDetails) {
            fetchContent();
        }
    }, [trialistDetails, getScenarioType]);

    useEffect(() => {
        if (!schoolid) return;
        getCountryId(schoolid)
            .then((response) => {
                setCountryId(response.countryId || null);
            })
            .catch(() => setCountryId(null));
    }, [schoolid]);

    // Fetch banner content
    useEffect(() => {
        if (!purchasesLoaded) return;

        const fetchActiveBanners = async () => {
            try {
                // Get banners for this country, fallback to global
                let banners = await getActiveBannerContents(null, countryId);
                if (!banners || banners.length === 0) {
                    banners = await getActiveBannerContents(null, null);
                }

                if (banners && banners.length > 0) {
                    // Subscriber renewal banner — only applies to subscriber_renewal scenario
                    if (scenarioType === 'subscriber_renewal') {
                        let renewalBanner = banners.find(b => b.scenario_type === 'subscriber_renewal');
                        if (!renewalBanner) {
                            renewalBanner = banners.find(b => b.id === '88f4dbb0-ef42-46cf-a3f1-5661fcacfa23'); // UUID fallback during transition
                        }

                        if (renewalBanner && renewalBanner.show_days_before != null) {
                            setLeadInDayThreshold(prev => prev ?? renewalBanner.show_days_before);

                            // Show banner if no active purchase (lapsed customer) OR within renewal window
                            if (daysUntilPayment === null || (daysUntilPayment >= 0 && daysUntilPayment <= renewalBanner.show_days_before)) {
                                setBannerContent(renewalBanner.content);
                            } else {
                                setBannerContent(null);
                            }
                        }
                    }

                    // Pilot purchase banner — only applies to pilot_purchase scenario
                    if (scenarioType === 'pilot_purchase') {
                        let pilotBanner = banners.find(b => b.scenario_type === 'pilot_purchase');
                        if (!pilotBanner) {
                            pilotBanner = banners.find(b => b.id === '40fd0fec-7b19-46e9-b7a9-15af315711ef'); // UUID fallback during transition
                        }

                        if (pilotBanner && pilotBanner.show_days_before != null) {
                            setLeadInDayThreshold(prev => prev ?? pilotBanner.show_days_before);

                            if (daysUntilPilotEnd !== null && daysUntilPilotEnd >= 0 && daysUntilPilotEnd <= pilotBanner.show_days_before) {
                                setBannerContent(pilotBanner.content);
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching active banners', err);
                setLeadInDayThreshold(null);
            }
        };

        fetchActiveBanners();
    }, [schoolid, trialistDetails, daysUntilPayment, daysUntilPilotEnd, purchasesLoaded, scenarioType]);

    // Extract from trialistDetails
    const {
        extension_applied = false,
        completed = false,
        expired = trialistDetails?.expired || false
    } = trialistDetails || {};

    // Auto-open modal on mount when autoOpen prop is true
    const hasAutoOpened = useRef(false);
    useEffect(() => {
        if (!autoOpen || hasAutoOpened.current || !bannerContent || !scenarioType) return;
        hasAutoOpened.current = true;
        if (scenarioType === 'trial_expired') {
            setShowExpiredModal(true);
        } else {
            setShowPurchaseModal(true);
        }
    }, [autoOpen, bannerContent, scenarioType]);

    // Modal handlers
    const handleExtendModal = () => setShowExtendModal(true);
    const handlePurchaseModal = () => setShowPurchaseModal(true);
    const handleExpiredModal = () => setShowExpiredModal(true);
    const handleModalClose = () => {
        setShowExtendModal(false);
        setShowPurchaseModal(false);
        setShowExpiredModal(false);
        setMessage("");
    };

    // Action handlers
    const handleApplyNow = async () => {
        setLoading(true);
        setMessage("");
        try {
            await applyTrialistExtension(schoolid);
            setMessage(bannerContent?.messages?.extension_success || "Extension applied successfully!");
        } catch (error) {
            setMessage(bannerContent?.messages?.extension_error || "Failed to apply for an extension. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleForwardSubmit = async () => {
        if (!/\S+@\S+\.\S+/.test(forwardEmail)) {
            setMessage("Please enter a valid email address.");
            return;
        }

        setLoading(true);
        setMessage("");
        try {
            await sendPurchaseEmail(forwardEmail, teacherName, bannerContent?.urls?.purchase_form);
            setMessage("Purchase email sent successfully!");
            setForwardEmail("");
        } catch (error) {
            setMessage("Failed to send purchase email. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleTrialistExpire = useCallback(async () => {
        setMessage("");
        try {
            await markTrialistExpired(schoolid);
        } catch (error) {
            console.error("Failed to mark trial as expired:", error);
        }
    }, [schoolid]);

    // Mark as expired when appropriate — only for pure trialists with no purchase history and no pilot
    useEffect(() => {
        if (trialistDetails && freeDaysLeft === 0 && !hasPreviousPurchase && !hasValidPurchaseDate && !isPilot) {
            handleTrialistExpire();
        }
    }, [freeDaysLeft, trialistDetails, handleTrialistExpire, hasPreviousPurchase, hasValidPurchaseDate, isPilot]);

    // Render pricing message (handles newlines in HTML content)
    const renderPricingMessage = (pricingHtml) => {
        if (!pricingHtml) return null;
        const htmlWithBreaks = pricingHtml.replace(/\n/g, '<br />');
        return <span dangerouslySetInnerHTML={{ __html: htmlWithBreaks }} />;
    };

    // Loading states
    if (!trialistDetails || contentLoading) {
        return <BigSpinner />;
    }

    // Hide if user has current purchase (unless renewal) or has an active pilot (unless pilot_purchase)
    if ((hasCurrentPurchase && scenarioType !== 'subscriber_renewal') || (hasValidPilotDate && scenarioType !== 'pilot_purchase')) {
        return null;
    }

    // Error state
    if (contentError || !bannerContent) {
        return null; // Fail silently - don't break the app
    }

    // Determine which scenario we're in
    const isTrialActive = scenarioType === "trial_active";
    const isTrialExpired = scenarioType === "trial_expired";
    const isSubscriberRenewal = scenarioType === "subscriber_renewal";
    const isPilotPurchase = scenarioType === "pilot_purchase";
    const isLapsedCustomer = scenarioType === "lapsed_customer";
    const isExpired = isTrialExpired || isLapsedCustomer;

    return (
        <>
            {/* Banner */}
            <div className="free-trial-banner">
                <div className="banner-content">
                    <p className="banner-message">
                        {isTrialActive && (
                            <>
                                <span dangerouslySetInnerHTML={{
                                    __html: interpolate(bannerContent.banner?.message, interpolationValues)
                                        .replace(/\{\{days\}\}/g, `<strong>${freeDaysLeft}</strong>`)
                                }} />
                                {!extension_applied && bannerContent.banner?.extension_prompt && (
                                    <> {bannerContent.banner.extension_prompt}</>
                                )}
                            </>
                        )}
                        {isExpired && interpolate(bannerContent.banner?.message, interpolationValues)}
                        {isSubscriberRenewal && (
                            <span dangerouslySetInnerHTML={{
                                __html: interpolate(bannerContent.banner?.message, interpolationValues)
                                    .replace(/\{\{days\}\}/g, `<strong>${daysUntilPayment ?? freeDaysLeft}</strong>`)
                            }} />
                        )}
                        {isPilotPurchase && (
                            <span dangerouslySetInnerHTML={{
                                __html: interpolate(bannerContent.banner?.message, interpolationValues)
                                    .replace(/\{\{days\}\}/g, `<strong>${daysUntilPilotEnd}</strong>`)
                            }} />
                        )}
                    </p>
                </div>

                {/* Buttons for active trial */}
                {isTrialActive && (
                    <div className="banner-buttons">
                        <button
                            className="extend-btn"
                            onClick={handleExtendModal}
                            disabled={extension_applied || completed}
                        >
                            {extension_applied
                                ? bannerContent.buttons?.extend?.applied_label
                                : bannerContent.buttons?.extend?.label}
                        </button>
                        <button
                            className="purchase-btn"
                            onClick={handlePurchaseModal}
                        >
                            {bannerContent.buttons?.purchase?.label}
                        </button>
                    </div>
                )}
                {(isSubscriberRenewal || isPilotPurchase) && (
                    <div className="banner-buttons">
                        <button
                            className="purchase-btn"
                            onClick={handlePurchaseModal}
                        >
                            {bannerContent.buttons?.purchase?.label}
                        </button>
                    </div>
                )}
                {/* Buttons for expired states */}
                {isExpired && (
                    <div className="banner-buttons">
                        <button
                            className="expired-btn"
                            onClick={handleExpiredModal}
                        >
                            {bannerContent.buttons?.purchase?.label}
                        </button>
                    </div>
                )}
            </div>

            {/* Expired Modal (for trial_expired and lapsed_customer) */}
            <Modal show={showExpiredModal} onHide={handleModalClose} className="custom-expired-modal">
                <Modal.Header closeButton className="custom-modal-header"></Modal.Header>
                <Modal.Body className="custom-modal-body">
                    <h2 className="modal-title">
                        {interpolate(bannerContent.modals?.expired?.title, interpolationValues)}
                    </h2>
                    <p className="modal-subtitle">
                        {interpolate(bannerContent.modals?.expired?.subtitle, interpolationValues)}
                    </p>
                    <p className="pricing-info">
                        {renderPricingMessage(bannerContent.modals?.expired?.pricing_message)}
                    </p>
                </Modal.Body>
                <Modal.Footer className="custom-modal-footer">
                    <Button
                        variant="primary"
                        as="a"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={bannerContent.urls?.purchase_form}
                        className="purchase-btn-modal"
                    >
                        Purchase Form
                    </Button>
                    <div className="contact-support-container">
                        <Button
                            variant="light"
                            as="a"
                            href={`mailto:${bannerContent.urls?.support_email}`}
                            className="contact-support-btn"
                        >
                            Contact Support
                        </Button>
                        <p className="support-email-display">{bannerContent.urls?.support_email}</p>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Extend Trial Modal (only for trial_active) */}
            {isTrialActive && (
                <Modal show={showExtendModal} onHide={handleModalClose} className="custom-extend-modal">
                    <Modal.Header closeButton className="custom-modal-header"></Modal.Header>
                    <Modal.Body className="custom-modal-body">
                        <div className="trial-end-container">
                            <span className="trial-end-badge">Trial ends on</span>
                            <span className="trial-end-date">{interpolationValues.endDate}</span>
                        </div>
                        <h2 className="modal-title">
                            {bannerContent.modals?.extend?.title}
                        </h2>
                        <p className="modal-subtitle">
                            {bannerContent.modals?.extend?.subtitle && (
                                <>
                                    {bannerContent.modals.extend.subtitle.includes("let us know") ? (
                                        <>If you have any questions, <a href={`mailto:${bannerContent.urls?.support_email}`} className="contact-link">please let us know</a>.</>
                                    ) : (
                                        bannerContent.modals.extend.subtitle
                                    )}
                                </>
                            )}
                        </p>
                        {message && <p className="message">{message}</p>}
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <Button
                            variant="light"
                            className="apply-now-btn"
                            onClick={handleApplyNow}
                            disabled={loading || extension_applied}
                        >
                            {loading ? "Applying..." : "Apply now"}
                        </Button>
                        <Button
                            variant="primary"
                            as="a"
                            target="_blank"
                            href={bannerContent.urls?.purchase_form}
                            className="purchase-btn-modal"
                        >
                            Purchase Form
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Purchase Modal (for trial_active and renew_purchase) */}
            {(isTrialActive || isSubscriberRenewal || isPilotPurchase) && (
                <Modal show={showPurchaseModal} onHide={handleModalClose} className="custom-purchase-modal">
                    <Modal.Header closeButton className="custom-modal-header"></Modal.Header>
                    <Modal.Body className="custom-modal-body">
                        {isTrialActive && (
                            <div className="trial-end-container">
                                <span className="trial-end-badge">Trial ends on</span>
                                <span className="trial-end-date">{interpolationValues.endDate}</span>
                            </div>
                        )}
                        <h2 className="modal-title">
                            {interpolate(bannerContent.modals?.purchase?.title, interpolationValues)}
                        </h2>
                        <p className="modal-subtitle">
                            {interpolate(bannerContent.modals?.purchase?.subtitle, interpolationValues)}
                        </p>
                        <p className="pricing-info">
                            {renderPricingMessage(bannerContent.modals?.purchase?.pricing_message)}
                        </p>

                        <div className="purchase-button-container">
                            <Button
                                variant="primary"
                                as="a"
                                target="_blank"
                                rel="noopener noreferrer"
                                href={bannerContent.urls?.purchase_form}
                                className="purchase-btn-modal"
                            >
                                Purchase Form
                            </Button>
                        </div>
                        <div className="forward-section">
                            <hr className="divider" />
                            <p className="forward-message">Or forward message to purchasing person</p>
                            <input
                                type="email"
                                placeholder="name@school.com"
                                value={forwardEmail}
                                onChange={(e) => setForwardEmail(e.target.value)}
                                className="form-control custom-input"
                            />
                            <Button
                                variant="primary"
                                className="forward-btn-modal"
                                onClick={handleForwardSubmit}
                                disabled={loading}
                            >
                                {loading ? "Sending..." : "Forward"}
                            </Button>
                            {message && <p className="message">{message}</p>}
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
}

export default FreeTrialBanner;